import { ArrowNarrowUpRightIcon } from '@repo/studio-design-system-ui-icons/arrows/arrow-narrow-up-right'
import { ChevronLeftIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-left'
import { Pin02Icon } from '@repo/studio-design-system-ui-icons/general/pin-02'
import { MenuRow } from '@repo/studio-components-menu-row'
import * as React from 'react'
import {
  navigationSectionClusterIds,
  useStudioTranslate,
  useStudioNavigationConfig,
} from '@repo/studio-config/studio-config-context'
import {
  useStudioMatchPath,
  useStudioPathname,
  StudioLayoutLink,
} from '@repo/studio-config/studio-config-router'
import type {
  AppSwitchCluster,
  CategoryNavigationSection,
  HomeNavigationSection,
} from '@repo/studio-config/studio-config-context'
import { NsfwPlatformHidden } from '@repo/studio-config/nsfw-platform-hidden'
import { tw } from 'utils/classnames'
import type { Cluster, NavigationClusterId } from './navigation-cluster-data'
import {
  leftoverCategoryClusters,
  getVisibleClusterTools,
  useAfterHomeClusters,
  useNavigationSections,
  useClusterDefinitions,
  useClusteredItems,
  useNavigationOnlyItems,
  usePinnedToolsByCluster,
  useToolSections,
  useToolsByRoute,
} from './navigation-cluster-data'
import { NavigationItem } from './navigation-item'
import {
  SingularNavigationItems,
  NavigationHomeItem,
  NavigationSectionTitle,
} from './navigation-items'
import { NavigationClusterMenuItem } from './navigation-cluster'
import { StackedClusterRow } from './stacked-cluster-row'
import { PinnedToolsSideSection } from './pinned-tools-menu'

export function StackedNavigationItems({
  isParentOpen,
  onNavigate,
}: {
  isParentOpen: boolean
  onNavigate?: () => void
}) {
  const t = useStudioTranslate()
  const toolSections = useToolSections()
  const afterHomeClusters = useAfterHomeClusters()
  const clusteredItems = useClusteredItems()
  const clusterDefinitions = useClusterDefinitions()
  const navigationOnlyItems = useNavigationOnlyItems()
  const pinnedTools = usePinnedToolsByCluster()
  const navigationSections = useNavigationSections()
  const navigationConfig = useStudioNavigationConfig()
  const [pinnedToolsExpanded, setPinnedToolsExpanded] = React.useState(false)
  const [activeCluster, setActiveCluster] = React.useState<NavigationClusterId | null>(null)

  const handleNavigate = React.useCallback(() => {
    setActiveCluster(null)
    onNavigate?.()
  }, [onNavigate])

  const extraToolGroups = navigationConfig?.extraToolGroups
  // Synthetic, host-configured group ids have no `visibleToolKeys` or
  // `navigationOnlyItems` entries (those are keyed by real clusters), so the
  // lookups below skip them.
  const syntheticClusterIds = React.useMemo(
    () => new Set((extraToolGroups ?? []).map(group => group.id)),
    [extraToolGroups],
  )
  const isSyntheticActiveCluster = activeCluster ? syntheticClusterIds.has(activeCluster) : false

  const activeClusterClusteredItems = activeCluster ? clusteredItems[activeCluster] : undefined

  const visibleToolKeys = navigationConfig?.visibleToolKeys
  const activeAllowedKeys =
    activeCluster && !isSyntheticActiveCluster
      ? visibleToolKeys?.[activeCluster as AppSwitchCluster]
      : undefined
  const activeClusterItems = getVisibleClusterTools(
    activeClusterClusteredItems ?? [],
    activeAllowedKeys,
  )

  // Real clusters read their nav-only items from the static map (keyed by real
  // cluster). Synthetic, host-configured groups carry their custom items as the
  // computed cluster's `navigationOnlyItems` (see useToolSections), so resolve
  // those from the tool sections instead.
  const activeClusterNavOnlyItems = isSyntheticActiveCluster
    ? ([...toolSections.flatMap(section => section.clusters), ...afterHomeClusters].find(
        cluster => cluster.cluster === activeCluster,
      )?.navigationOnlyItems ?? [])
    : activeCluster
      ? (navigationOnlyItems[activeCluster as AppSwitchCluster] ?? [])
      : []

  const activeClusterDefinition = activeCluster
    ? clusterDefinitions.find(definition => definition.cluster === activeCluster)
    : undefined

  return (
    <div className="w-full overflow-x-hidden scroller-xs">
      <div
        className={tw(
          'flex w-[200%] translate-x-0 transition-transform duration-200 ease-out',
          activeCluster ? '-translate-x-1/2' : 'translate-x-0',
        )}
      >
        <div className="w-1/2 shrink-0">
          {!activeCluster ? (
            <div className="flex flex-col w-full">
              {navigationSections.map((section, sectionIndex) => {
                if (section.kind === 'home') {
                  return (
                    <StackedHomeSection
                      key="home"
                      section={section}
                      afterHomeClusters={afterHomeClusters}
                      onOpenCluster={setActiveCluster}
                      onNavigate={handleNavigate}
                    />
                  )
                }
                if (section.kind === 'pinned') {
                  return (
                    <StackedPinnedSection
                      key="pinned"
                      hasPinnedTools={pinnedTools !== null}
                      expanded={pinnedToolsExpanded}
                      onToggle={() => setPinnedToolsExpanded(!pinnedToolsExpanded)}
                    />
                  )
                }
                if (section.kind === 'category') {
                  const toolSection = toolSections[sectionIndex] ?? {
                    clusters: [],
                    unclusteredTools: [],
                  }
                  const hasSingularEntries = section.entries.some(
                    entry => entry.kind === 'singular',
                  )
                  if (
                    !hasSingularEntries &&
                    toolSection.clusters.length === 0 &&
                    toolSection.unclusteredTools.length === 0
                  ) {
                    return null
                  }
                  return (
                    <StackedCategorySection
                      key={sectionIndex}
                      section={section}
                      toolSection={toolSection}
                      activeCluster={activeCluster}
                      setActiveCluster={setActiveCluster}
                      onNavigate={handleNavigate}
                    />
                  )
                }
                return null
              })}

              {clusterDefinitions
                .filter(definition => {
                  // Synthetic, host-configured groups are surfaced explicitly in
                  // their section (see useToolSections), so never backfill them here.
                  if (syntheticClusterIds.has(definition.cluster)) {
                    return false
                  }
                  // Only the default Studio nav should backfill clusters that are not in NAVIGATION_SECTIONS.
                  // Custom app configs (e.g. Freemode) are explicit allowlists and must not be widened here.
                  if (navigationConfig) {
                    return false
                  }
                  let items = clusteredItems[definition.cluster] ?? []
                  // Synthetic groups were filtered out above, so this is a real cluster.
                  const allowedKeys = visibleToolKeys?.[definition.cluster as AppSwitchCluster]
                  if (allowedKeys) {
                    items = items.filter(item => allowedKeys.includes(item.toolType))
                  }
                  return items.length > 0
                })
                .filter(definition => {
                  return !navigationSections.some(section =>
                    navigationSectionClusterIds(section).includes(
                      definition.cluster as AppSwitchCluster,
                    ),
                  )
                })
                .map(definition => (
                  <div
                    key={definition.cluster}
                    className="py-3 border-b border-b-gray-800 flex flex-col gap-1"
                  >
                    <StackedClusterRow
                      definition={definition}
                      activeCluster={activeCluster}
                      onOpen={setActiveCluster}
                      onNavigate={handleNavigate}
                    />
                  </div>
                ))}
            </div>
          ) : null}
        </div>

        <div className="w-1/2 shrink-0">
          {activeCluster ? (
            <div className="flex flex-col w-full">
              <div className="pb-3 border-b border-b-gray-800">
                <BackItem onClick={() => setActiveCluster(null)} />
              </div>

              <div className="py-3 border-b border-b-gray-800">
                <div className="ml-2 typography-12 text-gray-500">
                  {activeClusterDefinition?.label}
                </div>
                {activeClusterDefinition?.route ? (
                  <AllToolsItem
                    title={activeClusterDefinition.allLabel}
                    route={activeClusterDefinition.route}
                  />
                ) : null}
                {activeClusterDefinition?.disabled ? (
                  <div className="ml-2 mt-2 typography-12 text-gray-500">
                    {activeClusterDefinition.disabledMessage}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1 mt-2">
                    {[...activeClusterItems, ...activeClusterNavOnlyItems]
                      .toSorted((a, b) => a.order - b.order)
                      .map(item => (
                        <NavigationItem
                          key={item.title}
                          title={item.title}
                          route={item.route}
                          expanded={true}
                          icon={item.icon}
                          onNavigate={handleNavigate}
                          tool={'toolType' in item ? item : undefined}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <NsfwPlatformHidden>
        {pinnedToolsExpanded && isParentOpen && pinnedTools ? (
          <PinnedToolsSideSection
            pinnedTools={pinnedTools}
            onClose={() => setPinnedToolsExpanded(false)}
            onNavigate={handleNavigate}
          />
        ) : null}
      </NsfwPlatformHidden>
    </div>
  )
}

function AllToolsItem({ title, route }: { title: string; route: string }) {
  const pathname = useStudioPathname()
  const matchPath = useStudioMatchPath()
  return (
    <MenuRow
      className="mb-4 mt-2"
      asChild
      variant="slim"
      key={title}
      selected={matchPath({ path: route, end: true }, pathname)}
    >
      <MenuRow.Link asChild>
        <StudioLayoutLink to={route}>
          <MenuRow.Label>{title}</MenuRow.Label>
          <MenuRow.Icon className="mx-1">
            <ArrowNarrowUpRightIcon />
          </MenuRow.Icon>
        </StudioLayoutLink>
      </MenuRow.Link>
    </MenuRow>
  )
}

function StackedCategorySection({
  section,
  toolSection,
  activeCluster,
  setActiveCluster,
  onNavigate,
}: {
  section: CategoryNavigationSection
  toolSection: ReturnType<typeof useToolSections>[number]
  activeCluster: NavigationClusterId | null
  setActiveCluster: (cluster: NavigationClusterId | null) => void
  onNavigate: () => void
}) {
  const leftoverClusters = leftoverCategoryClusters(section, toolSection.clusters)
  const toolsByRoute = useToolsByRoute()

  return (
    <div className="py-3 border-b border-b-gray-800 flex flex-col gap-1">
      {section.title ? (
        <span className="px-2 pb-2 typography-12 text-gray-500 line-clamp-1 overflow-clip h-[26px]">
          {section.title}
        </span>
      ) : section.category ? (
        <NavigationSectionTitle category={section.category} />
      ) : null}
      {section.entries.map(entry => {
        if (entry.kind === 'singular') {
          return (
            <NavigationItem
              key={entry.route}
              title={entry.title}
              route={entry.route}
              expanded={true}
              icon={entry.icon}
              exact={entry.exact}
              hardNavigation={entry.hardNavigation}
              onNavigate={onNavigate}
              tool={toolsByRoute.get(entry.route)}
            />
          )
        }
        const cluster = toolSection.clusters.find(item => item.cluster === entry.cluster)
        return cluster ? (
          <StackedClusterRow
            key={cluster.cluster}
            definition={cluster}
            activeCluster={activeCluster}
            onOpen={setActiveCluster}
            onNavigate={onNavigate}
          />
        ) : null
      })}
      {leftoverClusters.map(cluster => (
        <StackedClusterRow
          key={cluster.cluster}
          definition={cluster}
          activeCluster={activeCluster}
          onOpen={setActiveCluster}
          onNavigate={onNavigate}
        />
      ))}
      {toolSection.unclusteredTools.map(item => (
        <NavigationItem
          key={item.title}
          title={item.title}
          route={item.route}
          expanded={true}
          icon={item.icon}
          onNavigate={onNavigate}
          tool={item}
        />
      ))}
    </div>
  )
}

function StackedHomeSection({
  section,
  afterHomeClusters,
  onOpenCluster,
  onNavigate,
}: {
  section: HomeNavigationSection
  afterHomeClusters: Cluster[]
  onOpenCluster: (cluster: NavigationClusterId) => void
  onNavigate: () => void
}) {
  return (
    <>
      <div className="pb-3 border-b border-b-gray-800 flex flex-col gap-1">
        <NavigationHomeItem route={section.route} expanded={true} onNavigate={onNavigate} />
        <SingularNavigationItems items={section.items} expanded={true} onNavigate={onNavigate} />
      </div>
      {afterHomeClusters.length > 0 ? (
        <div className="py-3 border-b border-b-gray-800 flex flex-col gap-1">
          {afterHomeClusters.map(cluster => (
            <StackedClusterRow
              key={cluster.cluster}
              definition={cluster}
              activeCluster={null}
              onOpen={onOpenCluster}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : null}
    </>
  )
}

function StackedPinnedSection({
  hasPinnedTools,
  expanded,
  onToggle,
}: {
  hasPinnedTools: boolean
  expanded: boolean
  onToggle: () => void
}) {
  const t = useStudioTranslate()

  return (
    <NsfwPlatformHidden>
      {hasPinnedTools ? (
        <div className="py-3 border-b border-b-gray-800">
          <NavigationClusterMenuItem.Button
            label={t('studio.clusters.pinned')}
            icon={<Pin02Icon />}
            showLabel={true}
            isOpen={expanded}
            onClick={onToggle}
          />
        </div>
      ) : null}
    </NsfwPlatformHidden>
  )
}

function BackItem({ onClick }: { onClick: () => void }) {
  const t = useStudioTranslate()
  return (
    <MenuRow
      variant="slim"
      className={tw('cursor-pointer')}
      onClick={onClick}
      tabIndex={0}
      role="button"
    >
      <MenuRow.Icon>
        <ChevronLeftIcon />
      </MenuRow.Icon>
      <MenuRow.Label>{t('studio.common.back')}</MenuRow.Label>
    </MenuRow>
  )
}
